import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import WeatherPulseNav from "./WeatherPulseNav";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { CalendarCheck, Drop, Fire, Snowflake, Trophy } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";

/** Observe Weather 365 — one observation a day → your Personal Weather Year. */
export default function WeatherYear() {
  const { user, requireSignIn } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!user) return;
    http.get("/weather-pulse/my-year")
      .then(({ data }) => setData(data))
      .catch(() => setDenied(true));
  }, [user]);

  const calendarDays = useMemo(() => {
    if (!data) return [];
    const active = new Set(data.activity_dates || []);
    const days = [];
    const today = new Date();
    for (let i = 364; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      days.push({ iso, on: active.has(iso) });
    }
    return days;
  }, [data]);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-365-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="Observe Weather 365 — build a year-long observation habit, one day at a time." testidPrefix="ltbar-wp-year" />
        <div className="mt-4"><PageGuide pageKey="weather:365" testidPrefix="wp-365" /></div>
      </div>
      <div className="max-w-[1000px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ The challenge</div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Observe Weather 365.</h1>
        <p className="mt-2 text-sm md:text-base text-[#3A3830] max-w-2xl">
          One observation a day. Look outside, tap what you see, and after a year you'll hold your own Personal Weather Year — your hottest day, your rainiest stretches, your streaks. Educational, not competitive.
        </p>

        {!user ? (
          <div className="mt-8 rounded-2xl bg-white border border-black/10 p-8 text-center" data-testid="wp-365-signin">
            <Trophy size={30} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)]" />
            <p className="mt-2 text-sm text-[#3A3830]">Sign in to start your 365-day weather journal.</p>
            <button onClick={() => requireSignIn("join Observe Weather 365")} data-testid="wp-365-signin-btn"
              className="mt-4 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
              Sign in & begin
            </button>
          </div>
        ) : !data ? (
          <div className="mt-8 font-mono-tech text-[10px] tracking-[0.3em] uppercase">{denied ? "Couldn't load your year." : "Loading your year…"}</div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wp-365-stats">
              {[
                { icon: CalendarCheck, v: data.active_days, l: "Days observed", sub: `${data.challenge_pct}% of the year` },
                { icon: Fire, v: data.current_streak, l: "Current streak", sub: `best ${data.longest_streak} days` },
                { icon: Drop, v: data.rainy_days, l: "Rainy days logged", sub: `${data.total_rainfall_mm} mm measured` },
                { icon: Trophy, v: data.total_observations, l: "Total observations", sub: data.first_observation ? `since ${data.first_observation}` : "" },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl bg-white border border-black/10 p-4" data-testid={`wp-365-stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>
                  <s.icon size={18} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                  <div className="mt-1.5 font-serif-display text-3xl">{s.v}</div>
                  <div className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[color:var(--np-muted,#3A3830)]">{s.l}</div>
                  {s.sub && <div className="text-[11px] text-[#3A3830]/70 mt-0.5">{s.sub}</div>}
                </div>
              ))}
            </div>

            {/* Streak calendar */}
            <div className="mt-6 rounded-2xl bg-white border border-black/10 p-5" data-testid="wp-365-calendar">
              <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-3">Your last 365 days</div>
              <div className="grid gap-[3px]" style={{ gridTemplateColumns: "repeat(26, minmax(0,1fr))" }}>
                {calendarDays.map((d) => (
                  <div key={d.iso} title={d.iso}
                    data-testid={d.on ? `wp-365-day-on` : undefined}
                    className={`aspect-square rounded-[3px] ${d.on ? "bg-[color:var(--np-brand,#B85F2A)]" : "bg-black/[0.06]"}`} />
                ))}
              </div>
            </div>

            {/* Extremes + monthly rhythm */}
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="wp-365-extremes">
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-3">Your weather extremes</div>
                <ul className="space-y-1.5 text-sm">
                  {data.hottest && <li className="flex items-center gap-2"><Fire size={14} weight="fill" className="text-red-500" /> Hottest: <b>{data.hottest.value_c}°C</b> on {data.hottest.date}</li>}
                  {data.coldest && <li className="flex items-center gap-2"><Snowflake size={14} weight="fill" className="text-sky-600" /> Coldest: <b>{data.coldest.value_c}°C</b> on {data.coldest.date}</li>}
                  {!data.hottest && <li className="text-[#3A3830]/70">Add a temperature measurement to your reports to build this.</li>}
                </ul>
                {data.conditions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {data.conditions.slice(0, 8).map((c) => (
                      <span key={c.condition} className="inline-flex items-center gap-1 rounded-full border border-black/10 px-2.5 py-1 text-xs">
                        {c.emoji} {c.label} <b className="font-mono-tech text-[9px]">{c.count}</b>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="wp-365-monthly">
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-3">Monthly rhythm</div>
                {data.monthly.length > 0 ? (
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={data.monthly}>
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#B85F2A" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-[#3A3830]/70">Your monthly chart appears after your first observation.</p>
                )}
              </div>
            </div>

            <Link to="/weather-pulse" data-testid="wp-365-report-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
              Record today's weather
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
